import { FormikProps } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { isCreditAgreementMutateAllowedAdapter } from '@finley-technologies/credit-agreement-utils';

import {
  TAccountGQLType,
  OrgProvidedInputGQLType,
  OrganizationGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { BankAccountWithInstitutionAndOwnerFragmentGQLType } from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import {
  creditAgreementFullArtifactPartyMap,
  creditAgreementFullArtifactPropertyMap,
  propertyTypeContainerToPropertyMap,
} from '~/packages/creditAgreements/ui/consts';
import {
  createBankAccountsMap,
  createCapitalProvidersMap,
  createOrganizationIdToBankAccountsMap,
} from '~/packages/creditAgreements/ui/creditAgreementWip/utils/createMaps';
import { createCreditAgreementFullArtifactWipShell } from '~/packages/creditAgreements/ui/creditAgreementWip/utils/mutateWipMetadata';
import {
  CreditAgreement,
  DbTable,
  LoanProductsMap,
  OrganizationMap,
  OrganizationIdToUsersMap,
  CreditAgreementFullArtifactWipConfigType,
} from '~/packages/creditAgreements/ui/types';

// Helper functions for extracting data from WIP
const getOrganizationIdToUsersMapFromWip = (
  creditAgreementWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): OrganizationIdToUsersMap => {
  const usersMap: OrganizationIdToUsersMap = {};
  _.values(creditAgreementWip.facilityMechanics.parties).forEach((parties) => {
    parties.forEach((party) => {
      const orgId = party.entity.id;
      if (!usersMap[orgId]) {
        usersMap[orgId] = [];
      }
      party.users.forEach((userEntity) => {
        usersMap[orgId].push({
          id: userEntity.entity.id,
          firstName: userEntity.entity.firstName,
          lastName: userEntity.entity.lastName,
          email: userEntity.entity.email,
        });
      });
    });
  });
  return usersMap;
};

const getArtifactSlugsFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): string[] => {
  const artifactSlugs = new Set<string>();
  const allDeliverableArtifactConfigs = _.flattenDeep(
    wip.deliverables.map((d) => _.concat(d.finalArtifacts, d.dependencies)),
  ).map((a) => a._config_);
  for (const config of allDeliverableArtifactConfigs) {
    switch (config.type) {
      case 'supplementalTape':
        artifactSlugs.add(config.artifactSlug);
        break;
      default:
        break;
    }
  }
  return Array.from(artifactSlugs);
};

const getOrgProvidedKeysFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): string[] => {
  const { portfolioCovenants, deliverables } = wip;

  const orgProvidedKeys = new Set<string>();

  portfolioCovenants.forEach((cov) => {
    if (cov._config_) {
      cov._config_.postgresQueries.forEach((query) => {
        if (query.type === 'orgProvidedInput') {
          orgProvidedKeys.add(query.parameterConfig.key.key);
        }
      });
    }
  });

  const allDeliverableArtifactConfigs = _.flattenDeep(
    deliverables.map((d) => _.concat(d.finalArtifacts, d.dependencies)),
  ).map((a) => a._config_);

  allDeliverableArtifactConfigs.forEach((config) => {
    if (config.type === 'orgProvidedInput') {
      orgProvidedKeys.add(config.key);
    }
  });

  return Array.from(orgProvidedKeys);
};

const getOrganizationsFromWip = (
  wip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
): Pick<OrganizationGQLType, 'id' | 'name' | 'slug'>[] => {
  const orgs: Pick<OrganizationGQLType, 'id' | 'name' | 'slug'>[] = [];

  Object.values(wip.facilityMechanics.parties).forEach((parties) => {
    parties.forEach((party) => {
      orgs.push(party.entity);
    });
  });
  return orgs;
};

const getAllOrgProvidedInputKeysForUserAndFromWip = ({
  userOrgProvidedInputs,
  creditAgreementWip,
}: {
  userOrgProvidedInputs: OrgProvidedInputGQLType[];
  creditAgreementWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
}): string[] => {
  const orgProvidedKeysFromWip = getOrgProvidedKeysFromWip(creditAgreementWip);
  const userOrgProvidedKeys = userOrgProvidedInputs.map(({ key }) => key);

  return Array.from(
    new Set([...orgProvidedKeysFromWip, ...userOrgProvidedKeys]),
  );
};

export type UseWipConfigParams = {
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  formProps: FormikProps<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>;
  organizationMap: OrganizationMap;
  tAccounts: TAccountGQLType[];
  loanProductsMap: LoanProductsMap;
  facilityType: DbTable.FacilityType;
  creditFacilityId: string;
  arePartiesLocked: boolean;
  setArePartiesLocked: React.Dispatch<React.SetStateAction<boolean>>;
  operationMode: CreditAgreement.OperationMode;
  saveCreditAgreementFullArtifactWip: CreditAgreementFullArtifactWipConfigType['saveCreditAgreementFullArtifactWip'];
  isSaveCreditAgreementFullArtifactWipLoading: boolean;
  saveAndUpdateConfigCreditAgreementWip: CreditAgreementFullArtifactWipConfigType['saveAndUpdateConfigCreditAgreementWip'];
  // Optional for cases where user org provided inputs are available
  orgProvidedInputKeys?: string[];
  // Required bank accounts data to be passed from higher scope
  bankAccountsWithInstitutionAndOwner: BankAccountWithInstitutionAndOwnerFragmentGQLType[];
};

export type UseWipConfigReturn = {
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  capitalProvidersMap: ReturnType<typeof createCapitalProvidersMap>;
  setCapitalProvidersMap: React.Dispatch<
    React.SetStateAction<ReturnType<typeof createCapitalProvidersMap>>
  >;
};

/**
 * Custom hook for creating wipConfig with shared logic between components
 * This centralizes the complex wipConfig creation logic that was duplicated
 * between CreditAgreementWip.tsx and CreditAgreementFullArtifactWithIntrospectionEntityFetch.tsx
 */
export const useWipConfig = ({
  creditAgreementFullArtifact,
  formProps,
  organizationMap,
  tAccounts,
  loanProductsMap,
  facilityType,
  creditFacilityId,
  arePartiesLocked,
  setArePartiesLocked,
  operationMode,
  saveCreditAgreementFullArtifactWip,
  isSaveCreditAgreementFullArtifactWipLoading,
  saveAndUpdateConfigCreditAgreementWip,
  orgProvidedInputKeys,
  bankAccountsWithInstitutionAndOwner,
}: UseWipConfigParams): UseWipConfigReturn => {
  const [capitalProvidersMap, setCapitalProvidersMap] = useState(
    createCapitalProvidersMap(
      creditAgreementFullArtifact.facilityMechanics.parties.capitalProviders,
    ),
  );

  // bankAccountsMap should represent bank accounts currently being used in the WIP
  const bankAccountsMap = createBankAccountsMap(
    creditAgreementFullArtifact.facilityMechanics.parties,
  );

  const organizationIdToUsersMap = getOrganizationIdToUsersMapFromWip(
    formProps.values,
  );

  const organizationIdToBankAccountsMap = createOrganizationIdToBankAccountsMap(
    bankAccountsWithInstitutionAndOwner,
  );

  const wipShell = createCreditAgreementFullArtifactWipShell({
    creditFacilityId,
    facilityType,
  });

  const portfolioCovenantKeys = creditAgreementFullArtifact.portfolioCovenants.map(
    (cov) => cov.covenantKey,
  );
  const financialCovenantKeys = creditAgreementFullArtifact.financialCovenants.map(
    (cov) => cov.covenantKey,
  );

  const reportNamesMap: { [K: string]: string[] } = {};
  const allReportConfigTypes =
    CreditAgreement.DomainEntity.ReportConfigTypeIterable;
  for (const reportConfigType of allReportConfigTypes) {
    const reportNames = creditAgreementFullArtifact.reportingMechanics
      .filter((report) => report.reportConfigTypes.includes(reportConfigType))
      .map((report) => report.name);
    reportNamesMap[reportConfigType] = reportNames;
  }

  // Use provided orgProvidedInputKeys or extract from WIP as fallback
  const finalOrgProvidedInputKeys =
    orgProvidedInputKeys ?? getOrgProvidedKeysFromWip(formProps.values);

  const wipConfig: CreditAgreementFullArtifactWipConfigType = {
    formProps,
    wipShell,
    capitalProvidersMap,
    creditAgreementFullArtifactPropertyMap,
    creditAgreementFullArtifactPartyMap,
    loanProductsMap,
    organizationMap,
    tAccounts,
    organizationIdToUsersMap,
    organizationIdToBankAccountsMap,
    propertyTypeContainerToPropertyMap,
    arePartiesLocked,
    orgProvidedInputKeys: finalOrgProvidedInputKeys,
    setArePartiesLocked,
    setCapitalProvidersMap,
    isMutateAllowed: isCreditAgreementMutateAllowedAdapter(operationMode),
    artifactSlugs: getArtifactSlugsFromWip(formProps.values),
    portfolioCovenantKeys,
    financialCovenantKeys,
    reportNamesMap,
    saveAndUpdateConfigCreditAgreementWip,
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
    operationMode,
    bankAccountsMap,
  };

  return {
    wipConfig,
    capitalProvidersMap,
    setCapitalProvidersMap,
  };
};

// Export helper functions for external use if needed
export {
  getOrganizationIdToUsersMapFromWip,
  getArtifactSlugsFromWip,
  getOrgProvidedKeysFromWip,
  getAllOrgProvidedInputKeysForUserAndFromWip,
  getOrganizationsFromWip,
};
